import { ref, watch, type Ref } from 'vue'
import { getPopularTodaySegmentPage } from '@/features/recommendations/api/popularToday'
import {
  FAIR_HOME_V2_POPULAR_LOAD_MORE_STEP,
  appendFairHomeV2ForYouPage,
  mapFairHomeV2ForYouCards,
  type FairHomeV2PopularCard,
} from './fair-home-v2-popular.data'

export type FairHomeV2ForYouStatus = 'off' | 'loading' | 'ready' | 'unavailable'

/**
 * Decision 13 — the personal Popular Today carousel pages INSIDE itself: page 1 is the row, «Show
 * more» asks `segment=for_you&page=N` (her cached ranked list). `unavailable` (404
 * `segment_not_pageable`, no For You row yet, or any failure of page 1) → the carousel keeps
 * `/goods/popular`. Off for a guest.
 */
export function useFairHomeV2PopularForYou(audience: Ref<string>, enabled: Ref<boolean>) {
  const status = ref<FairHomeV2ForYouStatus>('off')
  const cards = ref<FairHomeV2PopularCard[]>([])
  const page = ref(0)
  const hasMore = ref(false)
  const loadingMore = ref(false)
  let controller: AbortController | null = null

  async function load(nextPage: number) {
    controller?.abort()
    controller = new AbortController()
    const signal = controller.signal
    const response = await getPopularTodaySegmentPage(
      'for_you',
      nextPage,
      FAIR_HOME_V2_POPULAR_LOAD_MORE_STEP,
      audience.value,
      signal,
    )
    if (signal.aborted) return
    const mapped = mapFairHomeV2ForYouCards(response.data.segment?.products)
    cards.value = nextPage === 1 ? mapped : appendFairHomeV2ForYouPage(cards.value, mapped)
    page.value = nextPage
    hasMore.value = Boolean(response.data.has_more)
  }

  async function start() {
    if (!enabled.value) {
      status.value = 'off'
      cards.value = []
      return
    }
    status.value = 'loading'
    try {
      await load(1)
      status.value = cards.value.length ? 'ready' : 'unavailable'
    } catch (error) {
      if ((error as { name?: string })?.name === 'CanceledError') return
      // 404 segment_not_pageable is the expected answer for a shopper without a For You row.
      status.value = 'unavailable'
    }
  }

  async function loadMore() {
    if (status.value !== 'ready' || !hasMore.value || loadingMore.value) return
    loadingMore.value = true
    try {
      await load(page.value + 1)
    } catch (error) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        console.warn('Popular today: the next For You page did not load', error)
        hasMore.value = false
      }
    } finally {
      loadingMore.value = false
    }
  }

  watch([audience, enabled], () => void start(), { immediate: true })

  return { status, cards, hasMore, loadingMore, loadMore, refresh: start }
}
